import { type ComponentProps, type ReactNode, useId } from 'react';
import { CircleAlert } from 'lucide-react';

import { Label } from '@/components/Label/Label';
import { cn } from '@/util/utils';

type FieldProps = Omit<ComponentProps<'div'>, 'children'> & {
    label: ReactNode;
    /**
     * For a label drawn in another style: the uppercase label heads of the Add outfit editor
     * (3144:70687). Most fields keep the design's 14px medium label.
     */
    labelClassName?: string;
    /** Id of the control inside. Generated when omitted; read it back through `children`. */
    htmlFor?: string;
    required?: boolean;
    /** Helper text under the control. Turns destructive, with an icon, when `error` is set. */
    message?: ReactNode;
    /** The field is in its Error state. Sets aria-invalid on the control through `children`. */
    error?: boolean;
    /** A small control on the label's row, right-aligned: a "Forgot your password?" link, a unit toggle. */
    action?: ReactNode;
    children: (control: {
        id: string;
        'aria-invalid'?: true;
        'aria-describedby'?: string;
    }) => ReactNode;
};

/**
 * form-field, READ 131:169: a column with the label row 6px above the control and the
 * message 8px below it. The message is text-sm (14px), as the Input component draws it and the
 * prototype renders it; an error message is medium with a 13px alert icon (ALS-268). Required is an asterisk after the label, never a chip. In the Error
 * state the label, the control's border and the message all turn destructive, three signals
 * at three eye heights, so an errored field is findable while scrolling. The one state
 * machine serves inputs, selects and textareas: the control is rendered by the caller with
 * the props this component hands it.
 */
function Field({
    label,
    labelClassName,
    htmlFor,
    required,
    message,
    error,
    action,
    className,
    children,
    ...props
}: FieldProps) {
    const generatedId = useId();
    const id = htmlFor ?? generatedId;
    const messageId = message ? `${id}-message` : undefined;

    return (
        <div
            data-slot="field"
            data-state={error ? 'error' : undefined}
            className={cn('group flex min-w-0 flex-col gap-1.5', className)}
            {...props}
        >
            {action ? (
                // Wraps the action under the label when both do not fit, rather than breaking
                // the label and leaving its asterisk adrift (ALS-303, at 320px).
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <Label htmlFor={id} className={cn(labelClassName, error && 'text-destructive')}>
                        {label}
                        {required ? <RequiredMark /> : null}
                    </Label>
                    {action}
                </div>
            ) : (
                <Label htmlFor={id} className={cn(labelClassName, error && 'text-destructive')}>
                    {label}
                    {required ? <RequiredMark /> : null}
                </Label>
            )}
            {children({
                id,
                'aria-invalid': error ? true : undefined,
                'aria-describedby': messageId,
            })}
            {message ? (
                <p
                    id={messageId}
                    data-slot="field-message"
                    className={cn(
                        'mt-0.5 flex items-center gap-1 text-sm leading-5',
                        error ? 'text-destructive font-medium' : 'text-muted-foreground',
                    )}
                >
                    {error ? <CircleAlert aria-hidden className="size-[13px] shrink-0" /> : null}
                    {message}
                </p>
            ) : null}
        </div>
    );
}

function RequiredMark() {
    return (
        <span aria-hidden className="text-destructive">
            *
        </span>
    );
}

export { Field, type FieldProps };
